import React from "react";
import { BankRankings, Ranking } from "../../../../../api/apiTypes";

interface TabellaClassificaProps {
  metodologia: string;
  peso: string; // es. "10%"
  dati: { intermediario: string; valore: number }[];
  colonnaValore: string;
}

interface TabellaFinaleProps {
  dati: { intermediario: string; valore: number }[];
}

interface TabelleClassificheProps {
  finalRanking: BankRankings;
}

const TabellaClassifica: React.FC<TabellaClassificaProps> = ({
  metodologia,
  peso,
  dati,
  colonnaValore,
}) => {
  // Calcola valore massimo dei dati
  const maxValore = Math.max(...dati.map(d => d.valore));

  // Estrai peso numerico da stringa "10%" => 10
  const pesoNumerico = parseFloat(peso.replace("%", ""));

  return (
    <div className="flex-1 bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden">
      <div className="p-3 text-xs border-b border-gray-300">
        <p className="font-semibold">Metodologia: {metodologia}</p>
        <p className="font-semibold mt-1">Peso classifica finale: {peso}</p>
        <p className="font-semibold">
          BPV: {maxValore > 0 ? (pesoNumerico / maxValore).toFixed(2) : 0}
        </p>
      </div>
      <table className="w-full text-left table-auto">
        <thead>
          <tr className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold tracking-wider">
            <th className="py-2 px-4 border-b border-gray-300">Intermediario</th>
            <th className="py-2 px-4 border-b border-gray-300 text-center">{colonnaValore}</th>
          </tr>
        </thead>
        <tbody>
          {dati.map((riga, index) => (
            <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
              <td className="py-2 px-4 text-xs font-medium">{riga.intermediario}</td>
              <td className="py-2 px-4 text-xs text-center">
                {colonnaValore === "Num. Volte in prima fascia"
                  ? riga.valore
                  : riga.valore.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TabellaFinale: React.FC<TabellaFinaleProps> = ({ dati }) => (
  <div className="flex-1 bg-white border border-gray-300 rounded-md shadow-sm overflow-hidden">
    <div className="bg-textSecondary text-white text-center p-2 font-bold text-lg rounded-md">
      CLASSIFICA FINALE
    </div>
    <table className="w-full text-left table-auto">
      <thead>
        <tr className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold tracking-wider">
          <th className="text-center p-2 text-md font-bold w-1/2">
            Intermediario Negoziatore
          </th>
          <th className="text-center p-2 text-md font-bold w-1/2">
            Punteggio finale
          </th>
        </tr>
      </thead>
      <tbody>
        {dati.map((riga, index) => (
          <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
            <td className="py-2 px-4 text-xs font-medium">{riga.intermediario}</td>
            <td className="py-2 px-4 text-xs font-medium text-center">{riga.valore}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TabelleClassifiche: React.FC<TabelleClassificheProps> = ({ finalRanking }) => {
  const intermediari = Object.keys(finalRanking);

  const mapToArray = (key: keyof Ranking, round = false) =>
    intermediari
      .map(banca => ({
        intermediario: banca,
        valore: round ? Math.round(finalRanking[banca][key]) : finalRanking[banca][key],
      }))
      .sort((a, b) => b.valore - a.valore);

  return (
    <div className="mt-8 p-4 bg-gray-100 rounded-lg shadow-inner">
      <h2 className="text-xl font-bold mb-6 pl-2"> CLASSIFICHE </h2>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* CLASSIFICA SENZA SCENARI */}
        <div className="lg:w-1/4">
          <div className="bg-textSecondary text-white text-center p-2 font-bold text-base rounded-md">
            CLASSIFICHE SENZA SCENARI
          </div>
          <TabellaClassifica
            metodologia="Somma dei punteggi ottenuti dall'intermediario su ogni singolo driver*"
            peso="10%"
            dati={mapToArray("Classifica per punteggio totale senza scenario")}
            colonnaValore="PUNTEGGIO TOTALE"
          />
        </div>

        {/* CLASSIFICHE PER SCENARI */}
        <div className="lg:w-3/4 flex flex-col gap-4">
          <div className="bg-textSecondary text-white text-center p-2 font-bold text-base rounded-md">
            CLASSIFICHE PER SCENARI
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <TabellaClassifica
              metodologia="Classifica per punteggio totale di tutti gli scenari*"
              peso="40%"
              dati={mapToArray("Classifica per punteggio totale di tutti gli scenari")}
              colonnaValore="PUNTEGGIO TOTALE"
            />
            <TabellaClassifica
              metodologia="Classifica per posizione su ogni scenario*"
              peso="40%"
              dati={mapToArray("Classifica per posizione su ogni scenario")}
              colonnaValore="PUNTEGGIO TOTALE"
            />
            <TabellaClassifica
              metodologia="Classifica per numero di presenze nella prima fascia di eccellenza*"
              peso="10%"
              dati={mapToArray("Classifica per numero di presenze nella prima fascia di eccellenza", true)}
              colonnaValore="Num. Volte in prima fascia"
            />
          </div>
        </div>
      </div>

      {/* CLASSIFICA FINALE */}
      <div className="lg:w-1/4 mt-12">
        <TabellaFinale dati={mapToArray("Classifica finale", true)} />
      </div>
    </div>
  );
};

export default TabelleClassifiche;
